import { useState } from 'react';
import { RejectionRecord, ParsedIssue, verifyRejection } from '../../lib/api/intelligence';
import ParsedIssuesList from './ParsedIssuesList';

interface RejectionDetailPanelProps {
  rejection: RejectionRecord;
  onVerified?: (updated: RejectionRecord) => void;
}

export default function RejectionDetailPanel({ rejection, onVerified }: RejectionDetailPanelProps) {
  const [issues, setIssues] = useState<ParsedIssue[]>(rejection.parsed_issues);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verified, setVerified] = useState(rejection.parse_status === 'verified');

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyError(null);
    try {
      const updated = await verifyRejection(rejection.id, issues);
      setVerified(true);
      onVerified?.(updated);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const parseStatusBadge: Record<RejectionRecord['parse_status'], string> = {
    pending: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    parsed: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
    verified: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-200 dark:border-white/[0.05] px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Rejection Detail
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {rejection.agency} — {rejection.form_type}
            {rejection.rejection_date && (
              <span className="ml-2">{new Date(rejection.rejection_date).toLocaleDateString()}</span>
            )}
          </p>
        </div>
        <span
          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
            parseStatusBadge[verified ? 'verified' : rejection.parse_status]
          }`}
        >
          {verified ? 'Verified' : rejection.parse_status}
        </span>
      </div>

      <div className="p-6 space-y-6">
        {/* Side-by-side: raw notes vs snapshot */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
              Agency Rejection Notes
            </p>
            <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 p-4 text-sm text-red-800 dark:text-red-200 whitespace-pre-wrap max-h-64 overflow-y-auto">
              {rejection.raw_rejection_notes || 'No rejection notes recorded.'}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
              Submitted Form Snapshot
            </p>
            <div className="rounded-lg bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-gray-700 p-4 max-h-64 overflow-y-auto">
              {Object.keys(rejection.submitted_form_snapshot).length > 0 ? (
                <dl className="space-y-1">
                  {Object.entries(rejection.submitted_form_snapshot).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-2 gap-2 text-xs">
                      <dt className="font-medium text-gray-500 dark:text-gray-400 truncate">{k}</dt>
                      <dd className="text-gray-800 dark:text-gray-200 truncate">
                        {String(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">No snapshot available.</p>
              )}
            </div>
          </div>
        </div>

        {/* Parsed Issues */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
              AI-Parsed Issues ({issues.length})
            </p>
            {!verified && issues.length > 0 && (
              <button
                onClick={handleVerify}
                disabled={verifying}
                className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60"
              >
                {verifying ? 'Verifying...' : 'Verify Parsing'}
              </button>
            )}
            {verified && (
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                Parsing verified
              </span>
            )}
          </div>

          {verifyError && (
            <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">
              {verifyError}
            </div>
          )}

          <ParsedIssuesList
            issues={issues}
            editable={!verified}
            onChange={setIssues}
          />
        </div>
      </div>
    </div>
  );
}
